/* Skin Clique — Customer Persona Selector (PROV-4986)
 *
 * Vanilla controller for sections/sc-persona-selector.liquid. Everything is
 * rendered server-side; this script only:
 *   1. switches the active persona (WAI-ARIA tabs pattern)
 *   2. toggles Starter Kit / Full Routine per persona
 *   3. adds every product in the active kit to the cart, then opens the
 *      theme's quick cart (via its documented `quick-cart:*` events)
 *
 * It is NOT part of the bundled theme.min.js `register()` framework, so it
 * self-initialises and re-initialises on `shopify:section:load` for the editor.
 */
(function () {
  "use strict";

  var ROOT_SELECTOR = "[data-sc-persona-selector]";
  var CART_ADD_URL = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root + "cart/add.js"
    : "/cart/add.js";

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  /* --- Persona tab switching --------------------------------------------- */
  function activatePersona(root, personaId) {
    var tabs = root.querySelectorAll("[data-persona-tab]");
    var panels = root.querySelectorAll("[data-persona-panel]");

    tabs.forEach(function (tab) {
      var active = tab.getAttribute("data-persona-tab") === personaId;
      tab.setAttribute("aria-selected", active ? "true" : "false");
      tab.setAttribute("tabindex", active ? "0" : "-1");
      tab.classList.toggle("is-active", active);
    });

    panels.forEach(function (panel) {
      var active = panel.getAttribute("data-persona-panel") === personaId;
      panel.classList.toggle("is-active", active);
      if (active) {
        panel.removeAttribute("hidden");
      } else {
        panel.setAttribute("hidden", "");
      }
    });
  }

  function wireTabs(root) {
    var tablist = root.querySelector("[data-persona-tabs]");
    if (!tablist) return;
    var tabs = Array.prototype.slice.call(tablist.querySelectorAll("[data-persona-tab]"));

    tablist.addEventListener("click", function (evt) {
      var tab = evt.target.closest("[data-persona-tab]");
      if (!tab) return;
      activatePersona(root, tab.getAttribute("data-persona-tab"));
    });

    tablist.addEventListener("keydown", function (evt) {
      var current = evt.target.closest("[data-persona-tab]");
      if (!current) return;
      var index = tabs.indexOf(current);
      var next = null;

      switch (evt.key) {
        case "ArrowRight":
        case "ArrowDown":
          next = tabs[(index + 1) % tabs.length];
          break;
        case "ArrowLeft":
        case "ArrowUp":
          next = tabs[(index - 1 + tabs.length) % tabs.length];
          break;
        case "Home":
          next = tabs[0];
          break;
        case "End":
          next = tabs[tabs.length - 1];
          break;
        default:
          return;
      }

      evt.preventDefault();
      next.focus();
      activatePersona(root, next.getAttribute("data-persona-tab"));
    });
  }

  /* --- Kit toggle -------------------------------------------------------- */
  function wireKitToggles(root) {
    root.querySelectorAll("[data-kit-toggle]").forEach(function (toggle) {
      toggle.addEventListener("click", function (evt) {
        var btn = evt.target.closest("[data-kit-select]");
        if (!btn) return;
        var panel = btn.closest("[data-persona-panel]");
        if (!panel) return;
        var kit = btn.getAttribute("data-kit-select");
        panel.setAttribute("data-active-kit", kit);
        toggle.querySelectorAll("[data-kit-select]").forEach(function (b) {
          b.setAttribute("aria-pressed", b === btn ? "true" : "false");
        });
      });
    });
  }

  /* --- Add active kit to cart -------------------------------------------- */
  function parseIds(value) {
    return (value || "")
      .split(",")
      .map(function (id) { return id.trim(); })
      .filter(function (id) { return id !== "" && id !== "null"; });
  }

  function setStatus(button, message) {
    var status = button.querySelector(".sc-persona__add-all-status");
    if (status) status.textContent = message || "";
  }

  function wireAddAll(root) {
    root.querySelectorAll("[data-add-all]").forEach(function (button) {
      button.addEventListener("click", function () {
        var panel = button.closest("[data-persona-panel]");
        if (!panel) return;
        var kit = panel.getAttribute("data-active-kit") || "starter";
        if (button.classList.contains("is-loading")) return;

        // Preferred path: navigate to the curated cart permalink (exact bundle
        // variants + discount baked in). Most reliable — right variants, code
        // applied, lands on the cart. Matches what the skincare quiz does.
        var cartUrl = button.getAttribute("data-" + kit + "-cart");
        if (cartUrl) {
          button.classList.add("is-loading");
          button.setAttribute("aria-busy", "true");
          window.location.href = cartUrl;
          return;
        }

        // Fallback: AJAX-add each product's first available variant.
        var ids = parseIds(button.getAttribute("data-" + kit + "-ids"));
        if (!ids.length) {
          setStatus(button, "No products to add");
          return;
        }

        button.classList.add("is-loading");
        button.setAttribute("aria-busy", "true");
        setStatus(button, "");

        var payload = {
          items: ids.map(function (id) { return { id: id, quantity: 1 }; })
        };

        fetch(CART_ADD_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(payload)
        })
          .then(function (res) {
            if (!res.ok) return res.json().then(function (e) { throw e; });
            return res.json();
          })
          .then(function () {
            var code = button.getAttribute("data-discount-code");
            // Discount applies to the full routine only, never the starter kit.
            if (code && kit === "full") {
              window.location.href =
                "/discount/" + encodeURIComponent(code) + "?redirect=/cart";
              return;
            }
            // Automatic-discount path: refresh + open the theme's quick cart.
            emit("cart:updated");
            emit("quick-cart:updated");
            emit("quick-cart:open");
            button.classList.remove("is-loading");
            button.removeAttribute("aria-busy");
          })
          .catch(function (err) {
            button.classList.remove("is-loading");
            button.removeAttribute("aria-busy");
            var message = (err && (err.description || err.message)) || "Couldn't add to cart";
            setStatus(button, message);
            emit("cart:error", { errorMessage: message });
          });
      });
    });
  }

  /* --- Steps accordion (mobile) ------------------------------------------ */
  function wireStepsToggle(root) {
    root.querySelectorAll("[data-steps-toggle]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var expanded = btn.getAttribute("aria-expanded") === "true";
        btn.setAttribute("aria-expanded", expanded ? "false" : "true");
      });
    });
  }

  /* --- Init -------------------------------------------------------------- */
  function init(root) {
    if (!root || root.dataset.scPersonaReady === "true") return;
    root.dataset.scPersonaReady = "true";
    wireTabs(root);
    wireKitToggles(root);
    wireStepsToggle(root);
    wireAddAll(root);
  }

  function initAll(scope) {
    var container = scope && scope.querySelectorAll ? scope : document;
    if (container.matches && container.matches(ROOT_SELECTOR)) init(container);
    container.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme editor: sections are re-injected without a page reload.
  document.addEventListener("shopify:section:load", function (evt) {
    initAll(evt.target);
  });
  document.addEventListener("shopify:block:select", function (evt) {
    var root = evt.target.closest && evt.target.closest(ROOT_SELECTOR);
    var personaId = evt.target.getAttribute && (
      evt.target.getAttribute("data-persona-tab") ||
      evt.target.getAttribute("data-persona-panel")
    );
    // Reveal the persona the merchant is editing.
    if (root && personaId) activatePersona(root, personaId);
  });
})();
